import { connect } from '../base/connection/actions.web';
import { configureInitialDevices, getAvailableDevices } from '../base/devices/actions.web';
import { openDialog } from '../base/dialog/actions';
import { getBackendSafeRoomName } from '../base/util/uri';
import { isPrejoinPageVisible } from '../prejoin/functions';
import { DISMISS_CALENDAR_NOTIFICATION } from './actionTypes';
import LeaveReasonDialog from './components/web/LeaveReasonDialog.web';
import { markJoin, noteJoinPrestarted } from './joinMarks.web';
import logger from './logger';
/**
 * Opens {@code LeaveReasonDialog}.
 *
 * @param {string} [title] - The dialog title.
 *
 * @returns {Promise} Resolved when the dialog is closed.
 */
export function openLeaveReasonDialog(title) {
    return (dispatch) => new Promise(resolve => {
        dispatch(openDialog(LeaveReasonDialog, {
            onClose: resolve,
            title
        }));
    });
}
/**
 * Dismisses calendar notification about next or ongoing event.
 *
 * @returns {Object}
 */
export function dismissCalendarNotification() {
    return {
        type: DISMISS_CALENDAR_NOTIFICATION
    };
}
/**
 * Setups initial devices. Makes sure we populate availableDevices list before configuring.
 *
 * @returns {Promise<any>}
 */
export function setupInitialDevices() {
    return async (dispatch) => {
        markJoin('setupDevicesStart');
        const devices = await dispatch(getAvailableDevices());
        await dispatch(configureInitialDevices());
        markJoin('setupDevicesEnd');
        return devices;
    };
}
let prestartedJoin;
/** Whether this page has already started its initial join ahead of the screen. */
let joinPrestartAttempted = false;
/**
 * Starts a room join: signaling first, then the device inventory, then
 * APP.conference.init with that same signaling.
 *
 * XMPP needs no devices (the config and the room token are already in the
 * store), so it no longer waits for enumerateDevices and the audio output
 * setup. The camera and microphone request still follows the inventory, so
 * the exact camera, its label and default fallbacks and the substitution
 * notice are what they were.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {string} [room] - The backend-safe room name.
 * @param {Object} [early] - For a join started before the conference screen.
 * @param {Promise} [early.mounted] - Resolves when the screen mounts, since
 * startConference needs its UI.
 * @param {Function} [early.isCancelled] - Whether the join was abandoned
 * before its inventory was done; it then never asks for the camera.
 * @returns {Object} The signaling and the whole join.
 */
function startJoin(dispatch, room, early = {}) {
    markJoin('connectDispatched');
    const connection = Promise.resolve(dispatch(connect()));
    // The join consumes it; until APP.conference.init does, a signaling
    // failure must not also surface as an unhandled rejection.
    connection.catch(() => undefined);
    const started = dispatch(setupInitialDevices()).then(initialDevices => (early.isCancelled?.() ? undefined : APP.conference.init({
        connection,
        initialDevices,
        mounted: early.mounted,
        roomName: room
    }).catch((error) => {
        APP.API.notifyConferenceLeft(APP.conference.roomName);
        logger.error(error);
    })));
    return { connection, started };
}
/**
 * Starts the room join as soon as the route to the conference is decided,
 * before its screen renders (config.vrsPrestartJoin, on by default): the
 * Conference render and mount no longer sit in front of signaling and the
 * device inventory. Dispatched by the conference middleware on
 * APP_WILL_NAVIGATE for the Conference route, which comes after
 * getRouteToRender compared the page URL with the room's; CONNECTION_ESTABLISHED
 * strips that URL, so signaling must not start before the comparison.
 *
 * Not for prejoin (it connects on its own join), recorders or SIP gateways,
 * a page that already connects, or a second time on one page. The join still
 * waits for the screen before it enters the room (startConference needs
 * APP.UI), and init() adopts it, so there is one connection and one camera
 * request however the join began. A page that leaves first releases what
 * arrives through the same fence a hangup uses.
 *
 * @returns {Function} Resolves to whether a join was started.
 */
export function prestartJoin() {
    return (dispatch, getState) => {
        const state = getState();
        const config = state['features/base/config'];
        const { connecting, connection } = state['features/base/connection'];
        const room = getBackendSafeRoomName(state['features/base/conference'].room);
        if (joinPrestartAttempted || !room || config.vrsPrestartJoin === false
            || config.iAmRecorder || config.iAmSipGateway || connecting || connection
            || isPrejoinPageVisible(state)) {
            return false;
        }
        joinPrestartAttempted = true;
        let mount = () => undefined;
        const mounted = new Promise(resolve => {
            mount = resolve;
        });
        let signaling;
        let cancelled = false;
        const cancel = () => {
            cancelled = true;
            APP.conference.abandonInitialMedia();
            mount();
            signaling?.then((connection) => connection?.disconnect?.(), () => undefined);
        };
        // A page that leaves (a redirect, a closed tab) before its screen
        // mounts releases what it opened.
        const onPagehide = () => {
            if (prestartedJoin?.cancel === cancel) {
                prestartedJoin = undefined;
                cancel();
            }
        };
        window.addEventListener('pagehide', onPagehide, { once: true });
        noteJoinPrestarted();
        const join = startJoin(dispatch, room, {
            isCancelled: () => cancelled,
            mounted: mounted.then(() => {
                window.removeEventListener('pagehide', onPagehide);
            })
        });
        signaling = join.connection;
        prestartedJoin = { cancel, mount, room, started: join.started };
        return true;
    };
}
/**
 * Init.
 *
 * @returns {Promise<JitsiConnection>}
 */
export function init() {
    return (dispatch, getState) => {
        const state = getState();
        const room = getBackendSafeRoomName(state['features/base/conference'].room);
        const prestarted = prestartedJoin;
        prestartedJoin = undefined;
        if (prestarted) {
            if (prestarted.room === room) {
                prestarted.mount();
                return prestarted.started;
            }
            // Unreachable today: another room reloads the page. Never keep two
            // connections: abandon the early join, close its signaling, start over.
            logger.error('A join started for another room; starting this room\'s join instead');
            prestarted.cancel();
        }
        // Prejoin opens its own connection when the user joins from it.
        if (isPrejoinPageVisible(state)) {
            return dispatch(setupInitialDevices()).then(initialDevices => APP.conference.init({
                initialDevices,
                roomName: room
            }).catch((error) => {
                APP.API.notifyConferenceLeft(APP.conference.roomName);
                logger.error(error);
            }));
        }
        return startJoin(dispatch, room).started;
    };
}
